'use client';

import { analyticsPromise, functions } from '@util/firebase';
import { logError } from '@util/logError';
import { useAuth } from 'context/AuthContext';
import { useConsent } from 'context/ConsentContext';
import { httpsCallable } from 'firebase/functions';
import Cookie from 'js-cookie';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import * as pixel from '../lib/fpixel';

function loadScriptOnce(
  id: string,
  src: string,
  attrs?: Record<string, string>
) {
  if (document.getElementById(id)) return null;
  const script = document.createElement('script');
  script.id = id;
  script.src = src;
  script.async = true;
  if (attrs)
    Object.entries(attrs).forEach(([key, value]) =>
      script.setAttribute(key, value)
    );
  document.head.appendChild(script);
  return script;
}

export default function ThirdPartyScripts() {
  const { allowAds, allowAnalytics } = useConsent();
  const fbPixelLoadedRef = useRef(false);
  const [fbReady, setFbReady] = useState(false);
  const pathname = usePathname();

  // FOR ADS CONSENT
  useEffect(() => {
    if (!allowAds) return;
    const onEmbed = pathname?.includes('embed');
    if (onEmbed) return;

    // Facebook Pixel
    const FB_PIXEL_ID = process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID;
    if (FB_PIXEL_ID && !fbPixelLoadedRef.current) {
      (window as any).__FB_PIXEL_ID__ = FB_PIXEL_ID; // fallback
      const script = loadScriptOnce('fb-pixel', '/scripts/fbpixel.js', {
        'data-pixel-id': FB_PIXEL_ID,
      });
      if (script) {
        script.onload = () => {
          fbPixelLoadedRef.current = true; // so we don't trigger this useEffect again
          setFbReady(true); // so we can trigger the useEffect below
        };
      }
    }

    // Hotjar
    loadScriptOnce('hot-jar', '/scripts/hotjar.js');

    // TikTok Pixel
    loadScriptOnce('tiktok-pixel', '/scripts/tiktokpixel.js');

    // Klaviyo
    if (!Cookie.get('uid')) {
      loadScriptOnce(
        'klaviyo-popup',
        'https://static.klaviyo.com/onsite/js/Nk4Jwu/klaviyo.js'
      );
    }

    // Customers.ai
    loadScriptOnce(
      'customers-ai',
      'https://mm-uxrv.com/js/mm_81bdd527-f70a-4b7d-8745-a49070f96a59-13750651.js'
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowAds]);

  // FOR ANALYTICS CONSENT
  useEffect(() => {
    if (!allowAnalytics) return;
    const onEmbed = pathname?.includes('embed');
    if (onEmbed) return;
    analyticsPromise(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowAnalytics]);

  useEffect(() => {
    if (!fbReady) return;
    const onEmbed = pathname?.includes('embed');
    if (onEmbed) return;
    pixel.pageview();
  }, [pathname, fbReady]);

  const { user, userDoc } = useAuth();
  useEffect(() => {
    if (!user || !userDoc) return;
    if (process.env.NODE_ENV !== 'production') return;
    const onEmbed = pathname?.includes('embed');
    if (onEmbed) return;
    httpsCallable(
      functions,
      'v2_viewedPage'
    )({
      page: pathname,
      page_alt:
        pathname?.includes('shop') &&
        ![
          'all',
          'all-ties',
          'atv-tires',
          'auctions',
          'curated',
          'dirt-bike-tires',
          'featured',
          'motorcycle-tires',
          'parts',
          'recent',
          'riding-gear',
          'sale',
          'utv-tires',
        ].some((p) => pathname?.includes(p))
          ? 'CategoriesPage'
          : undefined,
    }).catch((error) => {
      logError(error);
    });
  }, [pathname, user, userDoc]);

  return null;
}
